// Handles ERP API key state: masked key, modified date, and modified by info

import type {ApiKeyInfo, ApiKeyUpdateRequest} from "~/types/api-key-type.ts";
import {
    createSlice,
    type Draft,
    type PayloadAction
} from "@reduxjs/toolkit";

export interface ApiKeyState {
    info: ApiKeyInfo | null;
    meta: {
        fetchingApiKey: boolean,
        fetchedApiKey: boolean,
        updatingApiKey: boolean,
        forbidden: boolean,
        notSupported: boolean,
    };
    error?: string | null;
}

export const initialState: ApiKeyState = {
    info: null,
    meta: {
        fetchingApiKey: false,
        fetchedApiKey: false,
        updatingApiKey: false,
        forbidden: false,
        notSupported: false,
    },
    error: null
};

const slice = createSlice({
    name: "apiKey",
    initialState,
    reducers: {
        fetchApiKey: (draft: Draft<ApiKeyState>) => {
            draft.meta.fetchingApiKey = true;
            draft.meta.fetchedApiKey = false;
            draft.meta.updatingApiKey = false;
            draft.meta.forbidden = false;
            draft.meta.notSupported = false;
            draft.error = null;
        },
        updateApiKey: (draft, _action: PayloadAction<ApiKeyUpdateRequest>) => {
            draft.meta.updatingApiKey = true;
            draft.error = null;
        },
        // Store fetched API key info
        receiveApiKey: (draft: Draft<ApiKeyState>, {payload}: PayloadAction<ApiKeyInfo>) => {
            draft.info = payload;
            draft.meta.fetchingApiKey = false;
            draft.meta.fetchedApiKey = true;
            draft.meta.forbidden = false;
            draft.meta.notSupported = false;
            draft.error = null;
        },
        // Store updated API key info
        receiveUpdatedApiKey: (draft: Draft<ApiKeyState>, {payload}: PayloadAction<ApiKeyInfo>) => {
            draft.info = payload;
            draft.meta.updatingApiKey = false;
            draft.error = null;
        },
        receiveApiKeyFailed: (draft: Draft<ApiKeyState>, {payload}: PayloadAction<string>) => {
            draft.meta.fetchingApiKey = false;
            draft.meta.fetchedApiKey = false;
            draft.meta.forbidden = false;
            draft.meta.notSupported = false;
            draft.error = payload;
        },
        receiveUpdatedApiKeyFailed: (draft: Draft<ApiKeyState>, {payload}: PayloadAction<string>) => {
            draft.meta.updatingApiKey = false;
            draft.error = payload;
        },
        apiKeyForbidden: (draft: Draft<ApiKeyState>) => {
            draft.meta.fetchingApiKey = false;
            draft.meta.fetchedApiKey = false;
            draft.meta.updatingApiKey = false;
            draft.meta.forbidden = true;
            draft.meta.notSupported = false;
            draft.error = null;
        },
        apiKeyNotSupported: (draft: Draft<ApiKeyState>) => {
            draft.meta.fetchingApiKey = false;
            draft.meta.fetchedApiKey = false;
            draft.meta.updatingApiKey = false;
            draft.meta.forbidden = false;
            draft.meta.notSupported = true;
            draft.error = null;
        },
        clearApiKeyError: (draft: Draft<ApiKeyState>) => {
            draft.error = null;
        }
    }
});

export const actions = slice.actions;
export default slice.reducer;
